<template>
  <g v-if="gradientStops.length > 1">
    <!-- Connecting lines between gradient stops -->
    <path
      :d="linePath"
      fill="none"
      :stroke="isInherited ? '#f97316' : 'white'"
      stroke-width="2"
      stroke-dasharray="4 2"
      opacity="0.7"
    />
    
    <!-- Gradient stop pips (smaller than regular pips) -->
    <circle
      v-for="stop in gradientStops"
      :key="stop.id"
      :cx="stop.screenX"
      :cy="stop.screenY"
      :r="pipRadius"
      :fill="stop.color"
      :stroke="isInherited ? '#f97316' : 'white'"
      :stroke-width="isInherited ? 3 : 2"
      class="cursor-pointer"
      @mousedown.stop="handleStopMouseDown($event, stop)"
      @click.stop="handleStopClick(stop)"
    />
    
    <!-- Light name / inheritance indicator at first stop -->
    <text
      v-if="gradientStops.length > 0"
      :x="gradientStops[0].screenX"
      :y="gradientStops[0].screenY - pipRadius - 4"
      text-anchor="middle"
      :class="isInherited ? 'text-[9px] fill-orange-400 pointer-events-none' : 'text-[10px] fill-white pointer-events-none'"
    >
      {{ isInherited ? `From: ${inheritedFromSceneName}` : lightName }}
    </text>
  </g>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { hueHsbToCss } from '@/types/wledEffect'
import type { Light } from '@/stores/lights'

interface GradientStop {
  id: string
  position: number
  color: {
    hue: number
    saturation: number
    brightness: number
  }
}

interface EffectSettings {
  gradient?: {
    stops: GradientStop[]
  }
}

interface Props {
  light: Light
  effectSettings?: EffectSettings | null
  circleRadius: number
  circleCenterX: number
  circleCenterY: number
  isSelected: boolean
  selectedStopId?: string | null
  showLabel?: boolean
  isInherited?: boolean
  inheritedFromSceneName?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  showLabel: false,
  selectedStopId: null,
  effectSettings: null,
  isInherited: false,
  inheritedFromSceneName: null
})

const emit = defineEmits<{
  'stop-drag-start': [event: MouseEvent, light: Light, stopId: string, stopColor: { hue: number; saturation: number; brightness: number }]
  'stop-click': [light: Light, stopId: string]
}>()

// Smaller pip size for gradient stops
const pipRadius = 6

// Get the light name
const lightName = computed(() => props.light.name.split(' ').slice(0, 2).join(' '))

// Get gradient stops from scene config effect settings
const gradientStops = computed(() => {
  const settings = props.effectSettings
  if (!settings?.gradient?.stops || settings.gradient.stops.length < 2) {
    return []
  }
  
  // Sort stops by position
  const sortedStops = [...settings.gradient.stops].sort((a, b) => a.position - b.position)
  
  // Position each stop at its ACTUAL hue/sat position on the wheel
  // This allows free dragging after creation
  const result = sortedStops.map((stop) => {
    // Convert Hue bridge scale (0-65535) to radians
    const hueAngle = (stop.color.hue / 65535) * 2 * Math.PI
    
    // Saturation determines radius (0 = center, 254 = edge)
    const normalizedSat = stop.color.saturation / 254
    const radius = normalizedSat * props.circleRadius * 0.85 // 85% to stay inside
    
    // Calculate screen position at actual color location
    const screenX = props.circleCenterX + Math.cos(hueAngle) * radius
    const screenY = props.circleCenterY + Math.sin(hueAngle) * radius
    
    return {
      id: stop.id,
      position: stop.position,
      hue: stop.color.hue,
      saturation: stop.color.saturation,
      brightness: stop.color.brightness,
      color: hueHsbToCss(stop.color.hue, stop.color.saturation, stop.color.brightness),
      screenX,
      screenY
    }
  })
  
  return result
})

// Generate SVG path connecting all gradient stops
const linePath = computed(() => {
  if (gradientStops.value.length < 2) return ''
  
  // Sort by position in gradient
  const sorted = [...gradientStops.value].sort((a, b) => a.position - b.position)
  
  const pathParts = sorted.map((stop, index) => {
    const command = index === 0 ? 'M' : 'L'
    return `${command} ${stop.screenX} ${stop.screenY}`
  })
  
  return pathParts.join(' ')
})

function handleStopMouseDown(event: MouseEvent, stop: typeof gradientStops.value[0]) {
  emit('stop-drag-start', event, props.light, stop.id, {
    hue: stop.hue,
    saturation: stop.saturation,
    brightness: stop.brightness
  })
}

function handleStopClick(stop: typeof gradientStops.value[0]) {
  emit('stop-click', props.light, stop.id)
}
</script>
